<script setup lang="ts">
import { IOutcome } from "~/types/sports/outcome";
import Silk from "~/components/layout/TrackRacing/Shared/Silk.vue";
import EventMarket from "~/components/layout/TrackRacing/Shared/Market/EventMarket.vue";
import { useBetbook } from "~/store/betbook";
import { IMarket } from "~/types/sports/market";
import useRacing from "~/composables/TrackRacing/useRacing";
import Ratings from "../Ratings.vue";
import GreyhoundsAvatar from "../GreyhoundsAvatar.vue";
const store = useBetbook();
const { convertJockeyWeight } = useRacing();

interface Props {
	outcome: IOutcome;
	markets: IMarket[] | undefined;
	selectedEventId: number | undefined;
	isStartingPrice?: boolean;
	showAdditionalInfo?: boolean | true;
}
const props = defineProps<Props>();

const selectedEvent = computed(() => {
	return store.events.get(props.selectedEventId as number);
});
</script>
<template>
	<div v-if="selectedEvent?.sportId !== 'greyhounds'" class="flex items-center justify-center">
		<div class="text-center" v-if="outcome.additionalInfo?.Number">
			<span class="text-[11px] text-dark-700 dark:text-light-50 font-bold">{{ outcome.additionalInfo?.Number }}</span>
			<div v-if="outcome.additionalInfo?.Draw" class="mt-[2px] text-[11px] font-light">({{ outcome.additionalInfo?.Draw }})</div>
		</div>
		<div v-else>-</div>
	</div>

	<div
		:class="[
			'flex items-center justify-center px-1 h-[25px]',
			selectedEvent?.sportId !== 'greyhounds' ? 'border-l dark:border-dark-500 border-l-light-400 border-r border-r-light-400' : '',
		]"
	>
		<GreyhoundsAvatar v-if="selectedEvent?.sportId === 'greyhounds'" :draw-number="outcome.index" />
		<Silk v-else :image-location="outcome.additionalInfo?.ImageLocation" :is-event-active="selectedEvent?.isActive" :width="35" :height="28" />
	</div>

	<div class="text-xs font-bold my-auto">
		<div class="text-dark-700 dark:text-light-50 w-full break-words">
			{{ outcome.displayName }}
		</div>
		<div
			v-if="outcome.additionalInfo?.JockeyName && outcome.additionalInfo?.TrainerName"
			class="text-[10px] text-dark-300 dark:text-light-900 mt-[2px] font-light w-full"
		>
			{{ outcome.additionalInfo?.TrainerName }}/{{ outcome.additionalInfo?.JockeyName }}
		</div>
	</div>

	<div v-if="showAdditionalInfo" class="flex items-center justify-center my-auto text-xs font-light">
		{{ outcome.additionalInfo?.Age }}
	</div>

	<div v-if="showAdditionalInfo" class="flex items-center justify-center my-auto text-xs font-light">
		<span v-if="outcome.additionalInfo">
			{{ convertJockeyWeight(outcome.additionalInfo.Weight) }}
		</span>
	</div>

	<div v-if="showAdditionalInfo" class="flex items-center justify-center my-auto text-xs font-light">
		{{ outcome.additionalInfo?.Form }}
	</div>

	<div v-if="showAdditionalInfo" class="flex items-center my-auto text-xs font-light">
		<Ratings v-if="outcome?.additionalInfo?.StarRating && showAdditionalInfo" :ratings="+outcome.additionalInfo?.StarRating" />
	</div>

	<div
		v-for="(market, index) in markets?.filter((m) => m.shouldDisplay)"
		:class="[
			'flex justify-between items-end text-xs col-end-auto md:col-span-1 text-dark-700 dark:text-light-50 ',
			index + 1 !== markets?.length ? 'border-r border-r-light-300 dark:border-dark-500' : '',
		]"
	>
		<EventMarket
			v-if="!outcome.displayName.includes('VACANT')"
			:outcome="!outcome.isTradingActive ? outcome : ([...store.outcomes.values()].find((o) => o.marketId === market.marketId && o.displayName === outcome.displayName) as IOutcome) || {}"
			:eventId="market.eventId"
			:market="market"
			:isStartingPrice="market.additionalInfo?.UseStartingPrice === 'True' ? isStartingPrice : false"
			:key="`${market.eventId}`"
		/>
	</div>
</template>
